import { isDesktop } from '../../lib/platform.ts'

/**
 * Notificação do SISTEMA (a do Windows, no canto da tela), para quando o Orbind
 * está em segundo plano e o cartão dentro do app ninguém vê.
 *
 * - **Desktop (Tauri):** o plugin nativo de notificação, que o shell já
 *   registra (`tauri-plugin-notification`, permissão `notification:default`
 *   liberada para o domínio de produção). Sai como notificação do Windows com o
 *   nome do Orbind. O plugin não entrega o clique de volta no desktop — o
 *   cartão dentro do app continua lá para abrir a conversa.
 * - **Navegador:** a Notification API. Clicar traz a aba para a frente e abre
 *   a conversa. Só com permissão dada — o pedido sai de um clique da pessoa
 *   (`pedirPermissao`), nunca sozinho no meio de uma mensagem.
 *
 * A chamada ao Tauri vai por `__TAURI_INTERNALS__`, sem `@tauri-apps/api`: o
 * mesmo bundle roda no navegador (ver features/app/desktop.ts).
 */

type Invoke = (comando: string, argumentos?: Record<string, unknown>) => Promise<unknown>

function invokeDoDesktop(): Invoke | null {
  if (!isDesktop()) return null
  const w = window as unknown as { __TAURI_INTERNALS__?: { invoke?: Invoke } }
  return w.__TAURI_INTERNALS__?.invoke ?? null
}

export type EstadoDaPermissao = 'concedida' | 'pendente' | 'negada' | 'indisponivel'

/** Dá para mostrar notificação do sistema agora? */
export function estadoDaPermissao(): EstadoDaPermissao {
  // No desktop o Windows não pede permissão por app: está sempre liberada.
  if (invokeDoDesktop()) return 'concedida'
  if (typeof Notification === 'undefined') return 'indisponivel'
  if (Notification.permission === 'granted') return 'concedida'
  if (Notification.permission === 'denied') return 'negada'
  return 'pendente'
}

/** Pede a permissão do navegador. Chamar SÓ a partir de um clique. */
export async function pedirPermissao(): Promise<EstadoDaPermissao> {
  if (estadoDaPermissao() !== 'pendente') return estadoDaPermissao()
  try {
    await Notification.requestPermission()
  } catch {
    // navegador antigo com a API de callback: o estado abaixo já reflete
  }
  return estadoDaPermissao()
}

export type AvisoDoSistema = {
  titulo: string
  corpo: string
  /** Agrupa: mensagens da mesma conversa substituem a anterior. */
  grupo: string
  icone?: string | null
  aoClicar?: () => void
  /** Quem mandou e a conversa — o toast do desktop com resposta rápida precisa (SPEC-20260916-1256). */
  chat?: { channelId: string; autor: string; onde: string; direta: boolean }
  /**
   * Notificação de Item de Trabalho (SPEC-20260924-2111): o toast do desktop
   * sai com o botão "Abrir o item" e devolve o clique — ver avisos.rs.
   */
  item?: { workItemId: string }
}

/**
 * Freio contra enxurrada (pedido do usuário, 2026-09-16).
 *
 * Sem isto, dez mensagens seguidas viravam dez toasts do Windows empilhados.
 * Aqui cada CONVERSA só produz um toast por janela; o que chegar dentro dela
 * espera e sai num aviso só, com a contagem ("5 novas mensagens") e o texto da
 * última — do jeito que o mensageiro de referência faz. Nada se perde: o
 * cartão dentro do app e a lista de conversas continuam mostrando tudo.
 */
const JANELA_POR_CONVERSA_MS = 6_000
/** Espaço mínimo entre dois toasts, mesmo de conversas diferentes. */
const ESPACO_ENTRE_TOASTS_MS = 1_500

type Espera = {
  /** Quando saiu o último toast desta conversa. */
  ultimoEm: number
  /** Quantas mensagens chegaram depois dele e ainda não foram anunciadas. */
  represadas: number
  /** A mais recente — é o texto que vai no aviso resumido. */
  ultimoAviso: AvisoDoSistema | null
  timer: ReturnType<typeof setTimeout> | null
}

const porConversa = new Map<string, Espera>()
let ultimoToastEm = 0

/** Só para os testes manuais: esquece o que já foi anunciado. */
export function limparFreioDeNotificacao(): void {
  for (const espera of porConversa.values()) if (espera.timer) clearTimeout(espera.timer)
  porConversa.clear()
  ultimoToastEm = 0
}

/**
 * Mostra a notificação do sistema — só com a janela SEM foco. Com o Orbind na
 * frente, o cartão dentro do app já basta; duas notificações da mesma
 * mensagem é barulho.
 *
 * Mensagens em rajada não saem uma a uma: ver `JANELA_POR_CONVERSA_MS`.
 */
export function notificarNoSistema(aviso: AvisoDoSistema): void {
  if (document.hasFocus()) return

  const agora = Date.now()
  const espera = porConversa.get(aviso.grupo) ?? { ultimoEm: 0, represadas: 0, ultimoAviso: null, timer: null }
  porConversa.set(aviso.grupo, espera)

  const esperaDaConversa = Math.max(0, espera.ultimoEm + JANELA_POR_CONVERSA_MS - agora)
  const esperaGlobal = Math.max(0, ultimoToastEm + ESPACO_ENTRE_TOASTS_MS - agora)
  const atraso = Math.max(esperaDaConversa, esperaGlobal)

  if (atraso === 0 && espera.timer === null) {
    mostrar(aviso)
    espera.ultimoEm = Date.now()
    espera.represadas = 0
    espera.ultimoAviso = null
    return
  }

  // Dentro da janela: guarda a mais nova e deixa o timer anunciar o conjunto.
  espera.represadas += 1
  espera.ultimoAviso = aviso
  if (espera.timer !== null) return
  espera.timer = setTimeout(() => {
    espera.timer = null
    const guardado = espera.ultimoAviso
    const quantas = espera.represadas
    espera.represadas = 0
    espera.ultimoAviso = null
    // A pessoa voltou para o app enquanto esperava: o aviso perdeu a razão.
    if (!guardado || document.hasFocus()) return
    mostrar(quantas > 1 ? { ...guardado, corpo: `${quantas} novas mensagens · ${guardado.corpo}` } : guardado)
    espera.ultimoEm = Date.now()
  }, Math.max(atraso, 1))
}

/** Entrega de fato, sem freio nenhum. */
function mostrar(aviso: AvisoDoSistema): void {
  ultimoToastEm = Date.now()

  const invoke = invokeDoDesktop()
  if (invoke) {
    // Shell 0.3.1+: toast do Windows com a cara do WhatsApp — avatar, nome,
    // mensagem, campo "Digite uma resposta" e Enviar (SPEC-20260916-1256). A
    // resposta e o clique voltam pelos eventos `orbind://chat-*` (ver
    // AvisosDoChat). Shell antigo sem o comando: cai na notificação simples.
    // Notificação de item: toast próprio, sem campo de resposta (avisos.rs).
    const pedido = aviso.item
      ? invoke('aviso_notificacao', {
          itemId: aviso.item.workItemId,
          titulo: aviso.titulo,
          texto: aviso.corpo,
        })
      : aviso.chat
      ? invoke('aviso_chat', {
          canalId: aviso.chat.channelId,
          autor: aviso.chat.autor,
          onde: aviso.chat.direta ? null : aviso.chat.onde,
          texto: aviso.corpo,
          // absoluta: o Rust baixa o avatar para o temp (o toast só aceita arquivo local)
          foto: aviso.icone ? new URL(aviso.icone, window.location.origin).href : null,
        })
      : Promise.reject(new Error('sem dados para o toast próprio'))
    void pedido.catch(() =>
      invoke('plugin:notification|notify', {
        options: { title: aviso.titulo, body: aviso.corpo, group: aviso.grupo },
      }).catch(() => undefined),
    )
    return
  }

  if (estadoDaPermissao() !== 'concedida') return
  try {
    const nativa = new Notification(aviso.titulo, {
      body: aviso.corpo,
      tag: aviso.grupo,
      icon: aviso.icone ?? '/orbind.svg',
    })
    nativa.onclick = () => {
      window.focus()
      aviso.aoClicar?.()
      nativa.close()
    }
  } catch {
    // Ambiente sem o construtor (algumas webviews): fica o cartão no app.
  }
}
